import { GregorioScore } from '../gregorio-chant/structures/GregorioScore';
import { GregorianChantRenderer } from './GregorianChantRenderer';
import { GlyphMeasurer } from './GlyphMeasurer';
import { LyricsTextMeasurer } from './LyricsTextMeasurer';

import * as base64fonts from '../../fonts/base64.json';
import { jsPDF } from 'jspdf';

export class GregorianChantSVGRenderer extends GregorianChantRenderer {
  private _svg?: SVGSVGElement;
  private _svgClass = 'gregorio-chant';
  private _rootElement: HTMLElement;

  constructor(rootElement: HTMLElement) {
    super();
    this._rootElement = rootElement;
  }

  public destroy(): void {
    if (this._svg) {
      this._svg.remove();
      this._svg = undefined;
    }
  }

  public renderSvg(score: GregorioScore): void {
    if (!score || !score.context) {
      return;
    }

    this._context = score.context;

    if (!this._svg) {
      this._svg = this.createSvgElement(false);
    }

    while (this._svg.firstChild) {
      this._svg.removeChild(this._svg.firstChild);
    }


    this.prepareNotations(score);
    this.prepareLines(score);

    this.addWhiteBg();
    this.renderInternal(this._svg!);
  }

  addWhiteBg() {
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('x', '0');
    bg.setAttribute('y', '0');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#ffffff');
    this._svg?.appendChild(bg);
  }

  renderInternal(svg: SVGSVGElement): number {
    GlyphMeasurer.initiate();
    LyricsTextMeasurer.initiate();

    const root = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    root.setAttribute('class', 'root-group');

    for (const line of this.lines) {
      for (const fragment of line.createSvgFragments()) {
        root.appendChild(fragment);
      }
    }

    if (this._context.renderInteractiveElements) {
      for (const line of this.lines) {
        for (const fragment of line.createInteractiveSvgFragments()) {
          root.appendChild(fragment);
        }
      }
    }

    svg.appendChild(root);
    const size = root.getBBox();

    if (this._commentary) {
      this._commentary.performLayout(size.width);
      for (const fragment of this._commentary.createSvgFragments()) {
        svg.appendChild(fragment);
      }
    }

    const topPadding = -this.top;

    let height = 0;
    if (this.lines.length > 0) {
      const lastLine = this.lines[this.lines.length - 1];
      height = lastLine.bottom + this._context.lyricTextSizePx + this._context.chantLineTopPaddingPx;
      svg.setAttribute('height', String(height));
    }

    root.setAttribute('transform', `translate(0, ${topPadding})`);

    GlyphMeasurer.destroy();
    LyricsTextMeasurer.destroy();

    return height;
  }

  private createSvgElement(injectFonts: boolean): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    svg.setAttribute('version', '1.1');
    svg.setAttribute('class', this._svgClass);
    svg.setAttribute('width', '100%');

    if (injectFonts) {
      const defsEl = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defsEl.setAttribute('type', 'text/css');
      const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');

      styleEl.textContent = `
@font-face{
  font-family: "greextra";
  src:url(data:application/font-woff;charset=utf-8;base64,${base64fonts['greextra']}) format("woff"); 
}
@font-face{
  font-family: "greciliae";
  src:url(data:application/font-woff;charset=utf-8;base64,${base64fonts['greciliae']}) format("woff"); 
}`;

      if (this._context.nabcMode === 'gall') {
        styleEl.textContent += `
@font-face{
  font-family: "gregall";
  src:url(data:application/font-woff;charset=utf-8;base64,${base64fonts['gregall']}) format("woff"); 
}
`;
      } else if (this._context.nabcMode === 'laon') {
        styleEl.textContent += `
@font-face{
  font-family: "grelaon";
  src:url(data:application/font-woff;charset=utf-8;base64,${base64fonts['grelaon']}) format("woff"); 
}
`;
      }

      defsEl.appendChild(styleEl);
      svg.appendChild(defsEl);
    }

    if (this._context.debugMode) {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', '0');
      rect.setAttribute('y', '0');
      rect.setAttribute('width', '100%');
      rect.setAttribute('height', '100%');
      rect.setAttribute('fill', 'none');
      rect.setAttribute('stroke', 'red');
      rect.setAttribute('stroke-width', '1');
      svg?.appendChild(rect);
    }

    if (!svg) {
      throw Error('Could not create SVG element');
    }

    this._rootElement.appendChild(svg);

    return svg;
  }

  public async exportSvgRaw(): Promise<[string, number]> {
    const svg = this.createSvgElement(true);
    const height = this.renderInternal(svg);

    const svgData = svg!.outerHTML;
    if (!svgData) {
      throw Error('SVG data is empty');
    }
    this._rootElement.removeChild(svg);

    return [svgData, height];
  }

  private async getSvgBlob(): Promise<[Blob, number]> {
    const [svgData, height] = await this.exportSvgRaw();
    return [new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }), height];
  }

  public async exportSvg(fileName: string) {
    const [svgBlob, height] = await this.getSvgBlob();
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  public async putOnCanvas() {
    const [svgBlob, originalHeight] = await this.getSvgBlob();

    const horizPadding = 1 * this._context.pngQualityCoefficient;
    const canvas = document.createElement('canvas');
    document.body.appendChild(canvas);
    const canvasWidth = (this.width + horizPadding * 2) * this._context.pngQualityCoefficient;
    if (canvasWidth > 32767) {
      const maxQuality = Math.floor((32767.0 / (this.width + horizPadding * 2)) * 10);
      // eslint-disable-next-line max-len
      throw Error(`Cannot fit score on canvas using current quality setting of ${this._context.pngQualityCoefficient}. Maximum quality for this score is ${(maxQuality / 10).toFixed(1)}`);
    }
    const canvasHeight = originalHeight * this._context.pngQualityCoefficient;
    if (canvasHeight > 32767) {
      const maxQuality = Math.floor((32767.0 / originalHeight) * 10);
      // eslint-disable-next-line max-len
      throw Error(`Cannot fit score on canvas using current quality setting of ${this._context.pngQualityCoefficient}. Maximum quality for this score is ${(maxQuality / 10).toFixed(1)}`);
    }

    if (canvasHeight * canvasWidth > 16384 * 16384) {
      const maxQuality = Math.floor(Math.sqrt((16384 * 16384) / (canvasHeight * canvasWidth)) * 100);
      // eslint-disable-next-line max-len
      throw Error(`Cannot fit score on canvas using current quality setting of ${this._context.pngQualityCoefficient}. Maximum quality for this score is ${(maxQuality / 10).toFixed(1)}`);
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d')!;

    const DOMURL = window.URL || window.webkitURL || window;
    const img1 = new Image();
    const url = DOMURL.createObjectURL(svgBlob);
    await new Promise<void>((resolve) => {
      img1.src = url;
      img1.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
        console.error(event, source, lineno, colno, error);
      };
      img1.onload = () => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img1, 0, 0, this.width, originalHeight,
          horizPadding * this._context.pngQualityCoefficient, 
          0, canvas.width - (horizPadding * 2 * this._context.pngQualityCoefficient), canvas.height);

        DOMURL.revokeObjectURL(url);
        resolve();
      };
    });

    return canvas;
  }

  public async exportPngRaw() {
    const canvas = await this.putOnCanvas();
    return canvas.toDataURL('image/png', 1.0);
  }

  public async exportPng(fileName: string) {
    const canvas = await this.putOnCanvas();
    setTimeout(() => {
      const dataUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = dataUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      document.body.removeChild(canvas);
    });
  }

  private async exportPdfInternal() {
    const canvas = await this.putOnCanvas();
    // const canvasData = canvas.toDataURL('image/png', 1.0);
    const pageWidth = 446.46;
    const height = canvas.height * pageWidth / canvas.width;

    const orientation = height > pageWidth ? 'p' : 'l';
    const doc = new jsPDF(orientation, 'px', [446.46, height], true);

    doc.addImage(canvas, 0, 0, pageWidth, height);
    document.body.removeChild(canvas);

    return doc;
    // return {} as any;
  }

  public async exportPdfRaw() {
    const doc = await this.exportPdfInternal();
    return doc.output('datauristring');
  }

  public async exportPdf(filename: string) {
    const doc = await this.exportPdfInternal();

    return doc.save(filename);
  }
}
